import React from 'react';
import styled from '@emotion/styled';
import { CircleTopicBaseFragment } from '@modernhealth/circles-graphql/v2';
import { CircleTopicFormat } from '@modernhealth/circles-graphql';
import { CircleCard, CircleCardLink } from '@modernhealth/circles-web-components';
import { filterNullish } from '@modernhealth/filter-nullish';
import { ComponentTextSmall, ComponentTextXSmall } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { FormattedDisplayName } from 'react-intl';

import { useLanguageCode } from '../hooks/useLanguageCode';

import { TranslatedCircleFormat } from './TranslatedCircleFormat';
import { TranslatedDuration } from './TranslatedDuration';
import { CircleCardHeaderLink } from './CircleCardHeaderLink';
import { CirclesSeriesEyebrow } from './CircleSessionCard';
import { CircleStylizedIcon } from './CircleStylizedIcon';

export interface CircleStylizedCardProps {
    id: string;
    topic: {
        facilitators: { edges: Array<{ node?: { photo?: string | null } | null } | null> };
        name: string;
        slug?: string | null;
        format: CircleTopicFormat;
        duration?: number;
        languageCode?: string | null;
    };
    series: {
        name: string;
        accessType?: string | null;
    };
    showSeriesLink?: boolean;
}

const CircleStylizedCard: React.FC<CircleStylizedCardProps> = ({
    series,
    topic,
    children,
    id,
    showSeriesLink = false,
}) => {
    const languageCode = useLanguageCode();
    const isNotUsersLanguage = topic.languageCode && topic.languageCode !== languageCode;
    const photos = filterNullish(topic?.facilitators.edges.map(edge => edge?.node?.photo) ?? []);

    return (
        <CircleCard
            format={topic.format}
            eyebrow={
                <CirclesSeriesEyebrow series={series} showAccess showSeriesLink={showSeriesLink} />
            }
            photos={photos}
            header={
                <CircleCardHeaderLink
                    name={topic.name}
                    to={`/circles/${topic.slug}`}
                    aria-describedby={children ? `${id}-description` : null}
                />
            }
            body={
                <span id={`${id}-description`}>
                    <ComponentTextSmall marginTop={8}>
                        <TranslatedCircleFormat format={topic.format} />
                        {topic.duration ? (
                            <>
                                {` • `}
                                <TranslatedDuration minutes={topic.duration} />
                            </>
                        ) : null}
                    </ComponentTextSmall>
                </span>
            }
            badge={
                topic.languageCode && isNotUsersLanguage ? (
                    <BadgeContainer>
                        <CircleStylizedIcon name="globe" />
                        <ComponentTextXSmall>
                            <FormattedDisplayName type="language" value={topic.languageCode} />
                        </ComponentTextXSmall>
                    </BadgeContainer>
                ) : undefined
            }
        >
            {children ? (
                <CircleCardLink
                    component={<span id={`${id}-description`} aria-hidden css={{ marginTop: 8 }} />}
                >
                    {children}
                </CircleCardLink>
            ) : null}
        </CircleCard>
    );
};

export const CircleTopicCard: React.FC<{
    topic: Omit<CircleTopicBaseFragment, 'sessions' | 'rsvpQuestions'>;
    id: string;
    showSeriesLink?: boolean;
}> = ({ topic, id, showSeriesLink }) => {
    return (
        <CircleStylizedCard
            topic={topic}
            series={topic.series}
            id={id}
            showSeriesLink={showSeriesLink}
        />
    );
};

const BadgeContainer = styled.div({
    textTransform: 'uppercase',
    alignItems: 'center',
    display: 'flex',
    gap: 6,
    alignSelf: 'flex-start',
    justifySelf: 'flex-end',
    backgroundColor: colors.gray10,
    color: colors.gray85,
    borderRadius: 4,
    padding: '6px 8px',
});
